import React, { useState, useEffect, memo } from "react";
import { collection, getDocs, Timestamp } from "firebase/firestore";
import { db } from "../service/firebase";
import { useInView } from "react-intersection-observer";
import "../styles/Projects.css";

/* =============================
   Single Project Card
============================= */
const ProjectCard = memo(({ project, index }) => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
    rootMargin: "-50px",
  });

  return (
    <article
      ref={ref}
      className={`project-card ${inView ? "animate-in" : ""}`}
      style={{ "--delay": `${index * 150}ms` }}
    >
      <div className="project-visual">
        <div className="browser-frame">
          <div className="browser-header">
            <span className="dot red" />
            <span className="dot yellow" />
            <span className="dot green" />
          </div>

          <div className="image-wrapper">
            <img
              src={
                project.previewImage ||
                "https://via.placeholder.com/800x450"
              }
              alt={project.projectName}
              loading="lazy"
            />
            <div className="image-overlay" />
          </div>
        </div>
      </div>

      <div className="project-content">
        <div className="content-top">
          <h3 className="project-title">{project.projectName}</h3>

          {project.projectSkills?.length > 0 && (
            <div className="tech-stack">
              {project.projectSkills.slice(0, 4).map((skill) => (
                <span key={skill} className="tech-tag">
                  {skill}
                </span>
              ))}
            </div>
          )}

          <p className="project-desc">{project.projectDescription}</p>
        </div>

        <div className="action-row">
          {project.githubLink && (
            <a
              href={project.githubLink}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-secondary"
            >
              Code
            </a>
          )}

          {project.preview && (
            <a
              href={project.preview}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
            >
              Live Demo
            </a>
          )}

          {project.playStoreLink && (
            <a
              href={project.playStoreLink}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-secondary"
            >
              Play Store
            </a>
          )}
        </div>
      </div>
    </article>
  );
});

ProjectCard.displayName = "ProjectCard";

/* =============================
   Main Projects Section
============================= */
const Projects = ({ isDarkMode }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const snap = await getDocs(
          collection(db, "Portfolio", "Projects", "Projects")
        );

        const list = snap.docs.map((doc) => {
          const data = doc.data();
          return {
            _id: doc.id,
            ...data,
            createdAt:
              data.createdAt instanceof Timestamp
                ? data.createdAt.toDate()
                : null,
          };
        });

        list.sort((a, b) => {
          const idA = Number(a.id ?? 999);
          const idB = Number(b.id ?? 999);
          if (idA !== idB) return idA - idB;
          return (b.createdAt ?? 0) - (a.createdAt ?? 0);
        });

        setProjects(list);
      } catch (err) {
        console.error("🔥 Projects fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  return (
    <section
      id="projects"
      className={`projects-section ${
        isDarkMode ? "dark-mode" : "light-mode"
      }`}
    >
      <div className="projects-container">

        {/* =============================
            Centered Header (Skills-style)
        ============================= */}
        <header className="section-header">
          <h2 className="gradient-text">Projects</h2>
          <p className="section-subtitle">
            A curated collection of real-world web and mobile applications I’ve
            built using modern technologies.
          </p>
        </header>

        {/* =============================
            Projects Grid
        ============================= */}
        {loading ? (
          <div className="projects-grid">
            {[1, 2, 3].map((n) => (
              <div key={n} className="project-skeleton" />
            ))}
          </div>
        ) : (
          <div className="projects-grid">
            {projects.length > 0 ? (
              projects.map((project, index) => (
                <ProjectCard
                  key={project._id}
                  project={project}
                  index={index}
                />
              ))
            ) : (
              <div className="empty-state">No projects available</div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default Projects;
